import api from './client'

export interface InAppNotification {
  id: string
  userId: string
  type: string
  titleIt: string
  titleEn: string
  bodyIt?: string
  bodyEn?: string
  entityType?: string
  entityId?: string
  isRead: boolean
  createdAt: string
}

export interface NotifInboxResult {
  items: InAppNotification[]
  hasMore: boolean
  unreadCount: number
  nextCursor: string | null
}

// Destinazione unica per una notifica, usata SIA dal pannello notifiche SIA dal
// dropdown della navbar (prima divergevano: la pagina instradava su `type`, la
// navbar su `entityType`, con vocabolari diversi → molte notifiche senza link).
// Si instrada sul `type` specifico (sempre presente, è il NotifType del backend);
// `entityType` (categoria grossolana) resta come fallback. Null = nessuna destinazione.
const NOTIF_TYPE_LINK: Record<string, (id?: string) => string> = {
  // Logbook / achievement
  DIVE_MILESTONE: (id) => (id ? `/app/logbook/${id}` : '/app/logbook'),
  ACHIEVEMENT_EARNED: () => '/app/achievements',
  // Social / buddy / contatti
  BUDDY_REQUEST: () => '/app/social', BUDDY_ACCEPTED: () => '/app/social',
  CONTACT_REQUEST: () => '/app/social', CONTACT_ACCEPTED: () => '/app/social',
  NEW_FOLLOWER: (id) => (id ? `/app/divers/${id}` : '/app/community'),
  NEW_REVIEW: () => '/app/social',
  MESSAGE: () => '/app/chat',
  // Prenotazioni / idoneità
  BOOKING_CONFIRMED: () => '/app/bookings', BOOKING_CANCELLED: () => '/app/bookings',
  BOOKING_BLOCKED_UNFIT: () => '/app/medical',
  // Corsi / lezioni
  COURSE_CONFIRMED: () => '/app/courses', COURSE_COMPLETED: () => '/app/courses',
  ENROLLMENT_REQUESTED: () => '/app/courses',
  LESSON_REQUESTED: () => '/app/my-lessons', LESSON_ACCEPTED: () => '/app/my-lessons',
  LESSON_DECLINED: () => '/app/my-lessons', LESSON_COMPLETED: () => '/app/my-lessons',
  LESSON_CANCELLED: () => '/app/my-lessons', INSTRUCTOR_CONTACT_REQUEST: () => '/app/my-lessons',
  // Escursioni / centro
  EXCURSION_BOOKED: () => '/app/excursions', EXCURSION_REQUEST_RECEIVED: () => '/app/excursions',
  EXCURSION_CONFIRMED: () => '/app/excursions', EXCURSION_REJECTED: () => '/app/excursions',
  EXCURSION_CANCELLED: () => '/app/excursions',
  STAFF_JOIN_REQUESTED: () => '/app/centers?tab=staff-requests',
  STAFF_INVITE_RECEIVED: () => '/app/centers', STAFF_INVITE_ACCEPTED: () => '/app/centers',
  STAFF_JOIN_APPROVED: () => '/app/centers',
  // Eventi / associazioni / contenuti
  EVENT_INVITED: () => '/app/events',
  ASSOCIATION_COMMUNICATION: () => '/app/associations',
  ARTICLE_APPROVED: () => '/app/news',
  CONSERVATION_ALERT_CREATED: () => '/app/biology-center',
  MEDIA_READY: () => '/app/media',
  // Assicurazione / loyalty / wallet / marketplace / merch
  POLICY_EXPIRING: () => '/app/insurance',
  LOYALTY_TIER_UPGRADE: () => '/app/wallet',
  GIFT_CARD_PURCHASED: () => '/app/wallet', GIFT_CARD_REDEEMED: () => '/app/wallet',
  OFFER_ACCEPTED: () => '/app/marketplace',
  MERCH_ORDER_PAID: () => '/app/shop/orders', MERCH_ORDER_SHIPPED: () => '/app/shop/orders',
  MERCH_ORDER_DELIVERED: () => '/app/shop/orders', MERCH_ORDER_CANCELLED: () => '/app/shop/orders',
  // KYC / certificazioni / medico
  KYC_APPROVED: () => '/app/profile', KYC_REJECTED: () => '/app/profile',
  CERT_VERIFIED: () => '/app/profile', CERT_REJECTED: () => '/app/profile',
  CERT_REQUEST_RECEIVED: () => '/app/profile', CERT_REQUEST_SUBMITTED: () => '/app/profile',
  CERT_REQUEST_APPROVED: () => '/app/profile', CERT_REQUEST_REJECTED: () => '/app/profile',
  PROF_CERT_EXPIRING: () => '/app/profile',
  MED_CERT_EXPIRING: () => '/app/medical', CLEARANCE_EXPIRING: () => '/app/medical',
  // Attrezzatura / divemaster
  EQUIPMENT_MAINTENANCE_EXPIRED: () => '/app/profile?tab=attrezzatura',
  EQUIPMENT_MAINTENANCE_EXPIRING_SOON: () => '/app/profile?tab=attrezzatura',
  DIVEMASTER_APPLIED: () => '/app/profile', DIVEMASTER_APPROVED: () => '/app/profile',
  DIVEMASTER_SUSPENDED: () => '/app/profile',
}

// Fallback sulla categoria grossolana (entityType, minuscolo) per i type non mappati.
const NOTIF_ENTITY_LINK: Record<string, (id?: string) => string> = {
  dive: (id) => (id ? `/app/logbook/${id}` : '/app/logbook'),
  achievement: () => '/app/achievements',
  buddy_request: () => '/app/social', contact_request: () => '/app/social', post: () => '/app/social',
  user: (id) => (id ? `/app/divers/${id}` : '/app/community'),
  message: () => '/app/chat',
  booking: () => '/app/bookings', course: () => '/app/courses', lesson: () => '/app/my-lessons',
  event: () => '/app/events', insurance: () => '/app/insurance', loyalty: () => '/app/wallet',
  kyc: () => '/app/profile', certification: () => '/app/profile',
  med_cert_request: () => '/app/medical', staff_request: () => '/app/centers?tab=staff-requests',
  equipment: () => '/app/profile?tab=attrezzatura', merch_order: () => '/app/shop/orders',
  excursion: (id) => (id ? `/app/excursions/${id}` : '/app/excursions'), media: () => '/app/media',
}

/** Destinazione unica per una notifica (type → entityType fallback). Null se ignota. */
export function notificationLink(n: Pick<InAppNotification, 'type' | 'entityType' | 'entityId'>): string | null {
  const byType = NOTIF_TYPE_LINK[n.type]
  if (byType) return byType(n.entityId)
  const byEntity = n.entityType ? NOTIF_ENTITY_LINK[n.entityType.toLowerCase()] : undefined
  return byEntity ? byEntity(n.entityId) : null
}

export interface NotifPreferences {
  userId:              string
  chatEnabled:         boolean
  mentionsOnly:        boolean
  quietHoursStart:     number | null
  quietHoursEnd:       number | null
  achievementsEnabled: boolean
  bookingsEnabled:     boolean
  coursesEnabled:      boolean
  buddyEnabled:        boolean
  insuranceEnabled:    boolean
  loyaltyEnabled:      boolean
  eventsEnabled:       boolean
  socialEnabled:       boolean
  safetyEnabled:       boolean
  equipmentEnabled:    boolean
  merchEnabled:        boolean
}

export type NotifPreferencesPatch = Partial<Omit<NotifPreferences, 'userId'>>

export const notificationsApi = {
  inbox: (params?: { limit?: number; cursor?: string }) =>
    api.get<NotifInboxResult>('/v1/notifications/inbox', { params }).then(r => r.data),

  markRead: (id: string) =>
    api.post(`/v1/notifications/${id}/read`),

  markAllRead: () =>
    api.post('/v1/notifications/read-all'),

  getPreferences: () =>
    api.get<NotifPreferences>('/v1/notifications/preferences').then(r => r.data),

  updatePreferences: (data: NotifPreferencesPatch) =>
    api.patch<NotifPreferences>('/v1/notifications/preferences', data).then(r => r.data),
}
